import { useQuery } from "react-query";
import { request } from "../../../config/helpers/axios-instance";
import { throwIfRequestError } from "../../hooks/request-error";

/**
 * The exact query params this endpoint is sent, as a pure function.
 *
 * Exported and built separately from the hook so it can be tested without React or a
 * mocked react-query -- the same convention as buildTatFilterParams in
 * src/v2/ops/hooks/useTatOrders.js.
 *
 * `search` is OMITTED when empty rather than sent as "": an empty string is a filter FOR
 * empty on the server, not the absence of a filter, which is how a search box silently
 * returns nothing.
 */
export function buildSalesOrdersParams({ page = 1, size = 20, search = "" } = {}) {
    return { page, size, ...(search ? { search } : {}) };
}

export default function useSalesOrders({ page = 1, size = 20, search = "", enabled = true } = {}) {
    const params = buildSalesOrdersParams({ page, size, search });

    return useQuery(
        ["sales-orders", params],
        async () => {
            const result = await request({ url: "/sales/orders", method: "GET", params });
            throwIfRequestError(result);
            const body = result.data;
            if (!body?.success) throw body;

            const { pagination, orders } = body.data;
            return {
                orders: orders || [],
                pagination: pagination || { page, size, total: 0 },
                total: pagination?.total || 0,
            };
        },
        { keepPreviousData: true, retry: false, enabled }
    );
}
